import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Drawer, AppBar, Toolbar, Typography, IconButton, List, ListItemButton,
  ListItemIcon, ListItemText, Avatar, Menu, MenuItem, Badge, Divider, Collapse,
  Chip, LinearProgress, Select, FormControl, Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon, Dashboard, Assessment, CloudUpload, FindInPage, Build,
  LocalHospital, Speed, Description, Summarize, People, Settings, Notifications,
  ExpandLess, ExpandMore, Warning, Lock,
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { RootState, AppDispatch } from '../../store/store';
import { fetchNotifications } from '../../store/slices/notificationSlice';
import { useTranslation } from 'react-i18next';

const DRAWER_WIDTH = 280;

const menuItems = [
  { key: 'dashboard',   icon: <Dashboard />,     path: '/dashboard',  label: 'nav.dashboard' },
  { key: 'evidence',  icon: <CloudUpload />,   path: '/evidence',   label: 'nav.evidence' },
  {
    key: 'compliance',  icon: <Assessment />,     path: '/compliance', label: 'nav.compliance',
    children: [
      { key: 'iterations',      path: '/compliance/iterations',      label: 'nav.iterations' },
      { key: 'chapters',        path: '/compliance/chapters',        label: 'nav.chapters' },
     // { key: 'self-assessment', path: '/compliance/self-assessment', label: 'nav.selfAssessment' },
    ],
  },
  { key: 'gaps',      icon: <FindInPage />,    path: '/gaps',       label: 'nav.gaps' },
  { key: 'capa',      icon: <Build />,         path: '/capa',       label: 'nav.capa' },
  { key: 'amsp',      icon: <LocalHospital />, path: '/amsp',       label: 'nav.amsp' },
  { key: 'kpi',       icon: <Speed />,         path: '/kpi',        label: 'nav.kpi' },
  { key: 'templates', icon: <Description />,   path: '/templates',  label: 'nav.templates' },
  { key: 'reports',   icon: <Summarize />,     path: '/reports',    label: 'nav.reports' },
  { key: 'sentinel',  icon: <Warning />,       path: '/sentinel',   label: 'nav.sentinel' },
  { key: 'users',     icon: <People />,        path: '/users',      label: 'nav.users' },
  { key: 'settings',  icon: <Settings />,      path: '/settings',   label: 'nav.settings' },
];

const MainLayout: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate   = useNavigate();
  const location   = useLocation();
  const dispatch   = useDispatch<AppDispatch>();
  const { user, logout } = useAuth();
  const { unreadCount }  = useSelector((state: RootState) => state.notifications);

  // ── Cycle data: always read from the live Redux dashboard store ───────────
  //
  // ROOT CAUSE OF THE BUG:
  //   The sidebar was reading `user.cycles_remaining` which comes from the
  //   login-time API response and is stored in localStorage/auth state.
  //   That value is a snapshot taken at login — it NEVER updates while the
  //   user is logged in, so it showed "3" even after a cycle was consumed.
  //
  // FIX:
  //   Read `state.dashboard.cycleStatus` which is populated by
  //   fetchDashboardData() and always reflects the live DB value.
  //   We keep the `?? user?.cycles_remaining` fallback only for the brief
  //   window before the first dashboard fetch completes (cycleStatus === null).
  const cycleStatus     = useSelector((state: RootState) => state.dashboard.cycleStatus);
  const cyclesRemaining = cycleStatus?.remaining  ?? user?.cycles_remaining ?? null;
  const isOrgLocked     = cycleStatus?.isLocked   ?? user?.is_org_locked   ?? false;
  const lockReason      = cycleStatus?.lockReason ?? null;

  const [drawerOpen,   setDrawerOpen]   = useState(true);
  const [expandedMenu, setExpandedMenu] = useState<string | null>(null);
  const [anchorEl,     setAnchorEl]     = useState<null | HTMLElement>(null);
  const [isLocked,     setIsLocked]     = useState(false);

  // Poll notifications every minute
  useEffect(() => {
    dispatch(fetchNotifications());
    const interval = setInterval(() => dispatch(fetchNotifications()), 60_000);
    return () => clearInterval(interval);
  }, [dispatch]);

  // Listen for account-locked custom events fired by the API interceptor (423)
  useEffect(() => {
    const handler = () => setIsLocked(true);
    window.addEventListener('account-locked', handler);
    return () => window.removeEventListener('account-locked', handler);
  }, []);

  // Keep the full-screen lock overlay in sync with live Redux cycleStatus
  useEffect(() => {
    if (isOrgLocked) setIsLocked(true);
  }, [isOrgLocked]);

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
  };

  // Content protection (screenshot / copy prevention)
  useEffect(() => {
    const preventCopy       = (e: ClipboardEvent) => e.preventDefault();
    const preventScreenshot = (e: KeyboardEvent)  => {
      if (e.key === 'PrintScreen' || (e.ctrlKey && e.key === 'p')) e.preventDefault();
    };
    const preventRightClick = (e: MouseEvent) => e.preventDefault();
    document.addEventListener('copy',        preventCopy);
    document.addEventListener('keydown',     preventScreenshot);
    document.addEventListener('contextmenu', preventRightClick);
    return () => {
      document.removeEventListener('copy',        preventCopy);
      document.removeEventListener('keydown',     preventScreenshot);
      document.removeEventListener('contextmenu', preventRightClick);
    };
  }, []);

  const progressValue = cyclesRemaining !== null
    ? Math.max(10, (cyclesRemaining / Math.max(cyclesRemaining + 2, 1)) * 100)
    : 10;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* ── Sidebar ────────────────────────────────────────────────────── */}
      <Drawer
        variant="permanent"
        sx={{
          width: drawerOpen ? DRAWER_WIDTH : 64,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerOpen ? DRAWER_WIDTH : 64,
            transition: 'width 0.2s',
            overflowX: 'hidden',
            bgcolor: '#1B3A5C',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Logo — always visible (shrinks when drawer is collapsed) */}
          <Box
            component="img"
            src="/logo.jpeg"           
            alt="GenBioCa logo"
            sx={{
              width:  drawerOpen ? 36 : 32,
              height: drawerOpen ? 36 : 32,
              objectFit: 'contain',
              flexShrink: 0,
              borderRadius: '4px',
            }}
          />

          {/* Text — only visible when drawer is expanded */}
          {drawerOpen && (
            <Box>
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem' }}>
                GenBioCa
              </Typography>
              <Typography sx={{ color: '#90CAF9', fontSize: '0.7rem' }}>NABH 6th Edition</Typography>
            </Box>
          )}
        </Box>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

        {/* Cycle counter — reads from Redux dashboard state (live DB value), NOT user token */}
        {drawerOpen && user && (
          <Box
            sx={{
              p: 1.5, mx: 1, my: 1, borderRadius: 1,
              bgcolor: isOrgLocked ? '#C0392B' : 'rgba(255,255,255,0.08)',
            }}
          >
            <Typography sx={{ color: '#fff', fontSize: '0.7rem', fontWeight: 600 }}>
              {isOrgLocked
                ? '🔒 LOCKED'
                : cyclesRemaining !== null
                  ? `Cycles: ${cyclesRemaining} remaining`
                  : 'Loading cycles…'}
            </Typography>

            {isOrgLocked && (
              <Typography sx={{ color: '#FFB3B3', fontSize: '0.6rem', mt: 0.5 }}>
                {lockReason ?? 'Contact gbc@genbioca.com'}
              </Typography>
            )}

            {!isOrgLocked && cyclesRemaining !== null && (
              <LinearProgress
                variant="determinate"
                value={progressValue}
                sx={{
                  mt: 0.5, height: 4, borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.1)',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: cyclesRemaining <= 1 ? '#E67E22' : '#27AE60',
                  },
                }}
              />
            )}
          </Box>
        )}

        <List sx={{ px: 1 }}>
          {menuItems.map((item) => (
            <React.Fragment key={item.key}>
              <ListItemButton
                selected={location.pathname.startsWith(item.path)}
                onClick={() => {
                  if (item.children) {
                    setExpandedMenu(expandedMenu === item.key ? null : item.key);
                  } else {
                    navigate(item.path);
                  }
                }}
                sx={{
                  borderRadius: 1, mb: 0.3, color: '#B0BEC5',
                  '&.Mui-selected': {
                    bgcolor: 'rgba(46,117,182,0.3)', color: '#fff',
                    '& .MuiListItemIcon-root': { color: '#fff' },
                  },
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                  minHeight: 40, py: 0.5,
                }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 36, '& svg': { fontSize: '1.2rem' } }}>
                  {item.icon}
                </ListItemIcon>
                {drawerOpen && (
                  <ListItemText
                    primary={t(item.label)}
                    primaryTypographyProps={{ fontSize: '0.82rem' }}
                  />
                )}
                {item.children && drawerOpen && (
                  expandedMenu === item.key ? <ExpandLess /> : <ExpandMore />
                )}
              </ListItemButton>

              {item.children && drawerOpen && (
                <Collapse in={expandedMenu === item.key}>
                  <List disablePadding>
                    {item.children.map((child) => (
                      <ListItemButton
                        key={child.key}
                        onClick={() => navigate(child.path)}
                        sx={{
                          pl: 6, py: 0.3, borderRadius: 1, color: '#B0BEC5',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
                        }}
                      >
                        <ListItemText
                          primary={t(child.label)}
                          primaryTypographyProps={{ fontSize: '0.78rem' }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          ))}
        </List>
      </Drawer>

      {/* ── Main content ───────────────────────────────────────────────── */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <AppBar position="sticky" elevation={0} sx={{ bgcolor: '#fff', borderBottom: '1px solid #E0E0E0' }}>
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={() => setDrawerOpen(!drawerOpen)}><MenuIcon /></IconButton>
              <Typography variant="h6" color="primary" sx={{ fontSize: '1rem' }}>
                {user?.organization || 'GenBioCa NABH Platform'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select
                  value={i18n.language}
                  onChange={(e) => handleLanguageChange(e.target.value)}
                  sx={{ fontSize: '0.8rem' }}
                >
                  <MenuItem value="en">EN</MenuItem>
                  <MenuItem value="hi">हिंदी</MenuItem>
                  <MenuItem value="ta">தமிழ்</MenuItem>
                  <MenuItem value="te">తెలుగు</MenuItem>
                  <MenuItem value="mr">मराठी</MenuItem>
                  <MenuItem value="bn">বাংলা</MenuItem>
                  <MenuItem value="kn">ಕನ್ನಡ</MenuItem>
                  <MenuItem value="gu">ગુજરાતી</MenuItem>
                  <MenuItem value="ml">മലയാളം</MenuItem>
                </Select>
              </FormControl>

              <Tooltip title="Notifications">
                <IconButton onClick={() => navigate('/notifications')}>
                  <Badge badgeContent={unreadCount} color="error"><Notifications /></Badge>
                </IconButton>
              </Tooltip>

              <Chip
                avatar={<Avatar sx={{ bgcolor: '#1B3A5C' }}>{user?.first_name?.[0] || 'U'}</Avatar>}
                label={`${user?.first_name || 'User'} (${user?.role_code || ''})`}
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{ cursor: 'pointer' }}
              />
              <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
                <MenuItem onClick={() => { setAnchorEl(null); navigate('/settings'); }}>Settings</MenuItem>
                <MenuItem onClick={() => { setAnchorEl(null); logout(); navigate('/login'); }}>Logout</MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Full-screen lock overlay */}
        {isLocked && (
          <Box sx={{
            position: 'fixed', inset: 0, zIndex: 9999,
            bgcolor: 'rgba(0,0,0,0.85)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexDirection: 'column', gap: 2,
          }}>
            <Lock sx={{ fontSize: 80, color: '#E74C3C' }} />
            <Typography variant="h4" sx={{ color: '#fff' }}>Account Locked</Typography>
            <Typography sx={{ color: '#ccc', maxWidth: 400, textAlign: 'center' }}>
              All audit cycles have been exhausted. Please contact GenBioCa at{' '}
              <a href="mailto:gbc@genbioca.com" style={{ color: '#3498DB' }}>gbc@genbioca.com</a>{' '}
              to purchase additional cycles.
            </Typography>
            <Typography sx={{ color: '#888', fontSize: '0.8rem' }}>
              Read-only access to historical data is preserved.
            </Typography>
          </Box>
        )}

        <Box sx={{ flexGrow: 1, p: 3, bgcolor: '#F0F4F8', overflow: 'auto' }}>
          <Outlet />
        </Box>

        {/* Watermark */}
        <Box sx={{
          position: 'fixed', bottom: 8, right: 8, opacity: 0.15, pointerEvents: 'none',
          fontSize: '0.6rem', color: '#000', transform: 'rotate(-5deg)',
        }}>
          {user?.email} | {new Date().toISOString().split('T')[0]}
        </Box>
      </Box>
    </Box>
  );
};

export default MainLayout;